/**
 * Token handling — LV §133-137, Arch §16.
 *
 * The rule that shapes this file (Arch §16):
 *
 * > The frontend must not keep sensitive tokens in localStorage unnecessarily.
 *
 * So the access token lives in a **module-scoped variable**, not in `localStorage`
 * and not in `sessionStorage`. A token in web storage is readable by any script that
 * gets injected; one in a closure is not. The cost is that a page reload loses it and
 * the OIDC silent-renew flow has to run again — which is the correct trade for a
 * system whose records have to withstand an audit.
 *
 * `localStorage` is used for exactly one thing, which LV §8 permits: non-critical UI
 * preferences. Dark mode and the selected site. Nothing else.
 */

import type { Me } from "@/types/api";

/** LV §8 — the only permitted use of localStorage. */
const PREFERENCE_PREFIX = "dah.pref.";

let accessToken: string | null = null;
let expiresAt = 0;
let currentUser: Me | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToken(): string | null {
  if (accessToken && expiresAt > 0 && Date.now() >= expiresAt) {
    // Expired: withheld rather than sent, because the server would only refuse it. The
    // session itself stays — the user, their permissions. Ending it here is what put the
    // sign-in screen in front of someone whose renewal was a moment away, and left every
    // permission check failing after that renewal had succeeded (#411).
    return null;
  }
  return accessToken;
}

/**
 * Whether someone is signed in: a token is held, valid or waiting to be renewed.
 *
 * Not the same question as `isAuthenticated()`, which asks whether the token could be
 * sent right now. The sign-in screen has to key on this one — keyed on the other, it took
 * over every time a token expired, renewal or not (#411).
 */
export function hasSession(): boolean {
  return accessToken !== null;
}

export function setToken(token: string, expiresInSeconds?: number): void {
  accessToken = token;
  // A minute of slack, so a request in flight when the token expires does not fail
  // on the server side.
  expiresAt = expiresInSeconds ? Date.now() + (expiresInSeconds - 60) * 1000 : 0;
  notify();
}

export function setUser(me: Me | null): void {
  currentUser = me;
  notify();
}

export function getUser(): Me | null {
  return currentUser;
}

export function clearSession(): void {
  accessToken = null;
  expiresAt = 0;
  currentUser = null;
  notify();
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * Whether the user holds a permission.
 *
 * A **UI convenience only** (LV §134). The server checks every request, and this
 * function existing does not make the button it hides a security control:
 *
 * > `if (user.role === "ADMIN") showDeleteButton()` is a UI convenience, never
 * > authorisation.
 */
export function hasPermission(permission: string): boolean {
  return currentUser?.permissions.includes(permission) ?? false;
}

export function hasAnyPermission(...permissions: string[]): boolean {
  return permissions.some(hasPermission);
}

/** The sites the user may see. Used to default the site filter, never to authorise. */
export function scopedSiteIds(): string[] {
  return currentUser?.scoped_site_ids ?? [];
}

// ---------------------------------------------------------------- preferences (LV §8)
export type Theme = "light" | "dark" | "system";

export function readPreference(key: string): string | null {
  try {
    return localStorage.getItem(`${PREFERENCE_PREFIX}${key}`);
  } catch {
    // Storage can be unavailable in a private window or when quota is exhausted. A
    // missing preference is not worth failing a render over.
    return null;
  }
}

export function writePreference(key: string, value: string): void {
  try {
    localStorage.setItem(`${PREFERENCE_PREFIX}${key}`, value);
  } catch {
    // Ignored for the same reason.
  }
}

export function getTheme(): Theme {
  const stored = readPreference("theme");
  return stored === "light" || stored === "dark" ? stored : "system";
}

export function setTheme(theme: Theme): void {
  writePreference("theme", theme);
  applyTheme(theme);
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") {
    root.removeAttribute("data-theme");
    return;
  }
  root.setAttribute("data-theme", theme);
}

/**
 * German labels and the cycle order for the two display preferences.
 *
 * Here rather than in `app/App.tsx` because there are now two callers: the shell header
 * and the sign-in screen, which sits *outside* `App` in the tree (see `Root` in
 * `main.tsx`). Six German strings in two files is the drift this repository writes tests
 * against — `.claude`-adjacent rule or not, the same word has to mean the same state on
 * both screens, or a user learns the control twice.
 */
export const THEME_LABEL: Record<Theme, string> = {
  system: "System",
  light: "Hell",
  dark: "Dunkel",
};

export function nextTheme(theme: Theme): Theme {
  return theme === "system" ? "light" : theme === "light" ? "dark" : "system";
}

/**
 * Increased contrast — LV §193.
 *
 * The LV names four accessibility affordances of the prototype (Fokusdarstellung,
 * Skip Link, reduzierte Bewegung, erhöhte Kontrastoptionen) and requires that they
 * "dürfen bei der Neuentwicklung nicht verloren gehen". This is the fourth.
 *
 * Three states, exactly like the theme: "system" follows the operating system's
 * `prefers-contrast`, the other two override it. A plant tablet is read in daylight
 * on a walkway, where the OS setting is often wrong in both directions — so following
 * the system alone is not enough, and an explicit option is what §193 asks for.
 *
 * A UI preference, so `localStorage` is the permitted store (LV §8, §128).
 */
export type Contrast = "normal" | "more" | "system";

export function getContrast(): Contrast {
  const stored = readPreference("contrast");
  return stored === "normal" || stored === "more" ? stored : "system";
}

export function setContrast(contrast: Contrast): void {
  writePreference("contrast", contrast);
  applyContrast(contrast);
}

export function applyContrast(contrast: Contrast): void {
  const root = document.documentElement;
  if (contrast === "system") {
    root.removeAttribute("data-contrast");
    return;
  }
  root.setAttribute("data-contrast", contrast);
}

/** As `THEME_LABEL`: one definition, two screens. */
export const CONTRAST_LABEL: Record<Contrast, string> = {
  system: "System",
  normal: "Normal",
  more: "Erhöht",
};

/**
 * Same three-state cycle as the theme, so the two controls behave identically rather
 * than each needing to be learned. "System" is kept rather than dropped, and that is
 * the accessibility call: with it gone the app would stamp `normal` on someone whose
 * operating system asks for increased contrast, and quietly override an assistive
 * preference they set once for everything (LV §193).
 */
export function nextContrast(contrast: Contrast): Contrast {
  return contrast === "system" ? "normal" : contrast === "normal" ? "more" : "system";
}

/**
 * Apply both stored preferences to the document.
 *
 * Called from `main.tsx` **before** the first render, not from an effect inside `App`.
 * The sign-in screen stands in front of `App` in the tree, so a preference applied in
 * `App`'s effect does not reach it — a user who pinned "Kontrast: Erhöht" read the one
 * page they cannot skip at normal contrast, which makes it a setting rather than the
 * option LV §193 asks for. Running before the first paint also removes the frame of the
 * wrong theme the app itself used to show.
 *
 * Both calls only read `localStorage` and write an attribute; neither needs a session
 * or a router, which is why they can run this early.
 */
export function applyDisplayPreferences(): void {
  applyTheme(getTheme());
  applyContrast(getContrast());
}

/**
 * The last selected site, so a plant manager does not re-pick it every morning.
 *
 * **Per user, and only a site the user still holds** (#411). It used to be one key for
 * the whole browser: it survived sign-out and applied to whoever used the browser next —
 * on a shared plant tablet, or after a permission moved to another site. A filter on a
 * site the user does not hold answers every list with 200 and nothing in it, while the
 * dashboard shows real numbers for the same site: two screens contradicting each other,
 * neither reporting an error. So the key carries the user's id, and a remembered site
 * outside the user's scope is ignored rather than applied.
 */
export function getSelectedSite(): string | null {
  forgetBrowserWideSite();
  const key = siteKey();
  if (!key) return null;
  const stored = readPreference(key);
  return stored && scopedSiteIds().includes(stored) ? stored : null;
}

export function setSelectedSite(siteId: string | null): void {
  const key = siteKey();
  if (!key) return; // nobody signed in: there is no one to remember it for
  if (siteId) writePreference(key, siteId);
  else removePreference(key);
}

function siteKey(): string | null {
  const id = currentUser?.user.id;
  return id ? `site.${id}` : null;
}

/** The pre-#411 key, one site for everybody. Read by nothing, removed on sight. */
function forgetBrowserWideSite(): void {
  removePreference("site");
}

function removePreference(key: string): void {
  try {
    localStorage.removeItem(`${PREFERENCE_PREFIX}${key}`);
  } catch {
    // Ignored, as for `readPreference`.
  }
}
